/**
 * The level format.
 *
 * A map is plain JSON-serialisable data, authored offline and shipped to both
 * sides. The server builds its collision world from it; the client builds the
 * same collision world *and* renders it. Neither derives collision from mesh
 * geometry — the Worker has no GLB loader and never will (see world.ts).
 *
 * Three properties this format exists to guarantee:
 *
 *  - **Both sides build byte-identical collision from the same file.** There is
 *    exactly one function that turns map data into a `StaticWorld`, and both
 *    sides call it. A map is not "level geometry plus a collision mesh someone
 *    exported separately"; the solids *are* the level as far as simulation is
 *    concerned, and the meshes hang off them.
 *
 *  - **A map that would break the simulation is rejected at author time.**
 *    `MAX_SUBSTEP_DISTANCE` implies a minimum solid thickness, and a spawn
 *    point inside geometry means a player who cannot move. Both are trivial to
 *    author by accident and near-impossible to diagnose from the symptom, so
 *    the validator refuses them rather than letting them ship.
 *
 *  - **Two clients cannot disagree about which map they are on.** `mapHash`
 *    goes in the join handshake. Mismatched geometry is not a desync to detect
 *    later, it is a connection to refuse now — the same argument the protocol
 *    version already makes.
 */

import { DEFAULT_HULL, type Hull } from "./movement.js";
import {
  MIN_SOLID_THICKNESS,
  WORLD_MAX_XZ,
  WORLD_MAX_Y,
  WORLD_MIN_XZ,
  WORLD_MIN_Y,
} from "./rules.js";
import { StaticWorld, aabbOverlaps, boxFromCenter, type Aabb } from "./world.js";

/**
 * Bumped when the *meaning* of existing fields changes, not when fields are
 * added. It is checked by the validator, so an old map fails loudly at load
 * rather than producing subtly wrong geometry.
 */
export const MAP_FORMAT_VERSION = 1;

// ---------------------------------------------------------------------------
// Prop catalogue
// ---------------------------------------------------------------------------

/**
 * A kind of prop: crate, barrel, chair. The catalogue is shared data because
 * the hull is simulation input — a hider who possesses a prop takes its hull,
 * so client and server must agree on its dimensions to the last bit or the
 * hider rubber-bands the moment they disguise.
 *
 * `mesh` is the one field the server never reads. It is still hashed, because
 * a client rendering a crate where everyone else renders a barrel is a
 * fairness bug in a game built on visual deception, not a cosmetic difference.
 */
export interface PropDef {
  /** Wire identity, 1..255. 0 is reserved for "an undisguised player". */
  id: number;
  label: string;
  hull: Hull;
  /** Client-side asset key. Never loaded server-side. */
  mesh: string;
}

/** Catalogue keyed by `PropDef.id`, as passed to the validator and builder. */
export type PropCatalogue = ReadonlyMap<number, PropDef>;

export function propCatalogue(defs: readonly PropDef[]): PropCatalogue {
  const byId = new Map<number, PropDef>();
  for (const def of defs) byId.set(def.id, def);
  return byId;
}

// ---------------------------------------------------------------------------
// Map data
// ---------------------------------------------------------------------------

/**
 * A collision box, authored as a footprint centre plus extents.
 *
 * `y` is the **bottom** of the box, not its centre, matching the hull
 * convention in movement.ts — hulls are anchored at the feet so that ground
 * contact and prop placement need no offset arithmetic. `x` and `z` are
 * centres, because a wall is naturally described by where its middle runs.
 */
export interface MapSolid {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}

/**
 * A prop instance placed in the level.
 *
 * Props are collision solids like any other, so a crate is something you can
 * stand on before it is something you can hide as.
 *
 * Note for M3: when a hider possesses a prop instance, that instance's static
 * solid has to leave the world, or the hider walks around leaving their own
 * body behind as an obstacle. `buildWorld` returns the solids in a documented
 * order specifically so a possession system can address one by index.
 */
export interface MapProp {
  /** `PropDef.id` from the catalogue. */
  prop: number;
  x: number;
  y: number;
  z: number;
  /** Radians. Quantised on the wire by the same codec as player yaw. */
  yaw: number;
}

export type SpawnTeam = "hunter" | "hider" | "any";

export interface MapSpawn {
  x: number;
  y: number;
  z: number;
  yaw: number;
  team: SpawnTeam;
}

export interface GameMap {
  formatVersion: number;
  name: string;
  solids: MapSolid[];
  props: MapProp[];
  spawns: MapSpawn[];
}

// ---------------------------------------------------------------------------
// Building the collision world
// ---------------------------------------------------------------------------

export function solidToAabb(s: MapSolid): Aabb {
  return boxFromCenter(s.x, s.y, s.z, s.w, s.h, s.d);
}

export function propToAabb(p: MapProp, def: PropDef): Aabb {
  return {
    minX: p.x - def.hull.halfWidth,
    minY: p.y,
    minZ: p.z - def.hull.halfDepth,
    maxX: p.x + def.hull.halfWidth,
    maxY: p.y + def.hull.height,
    maxZ: p.z + def.hull.halfDepth,
  };
}

/**
 * The map's collision solids, in a fixed order: every `solids` entry first, in
 * authored order, then every `props` entry, in authored order.
 *
 * The order is part of the contract rather than an implementation detail. It
 * is what lets a prop instance be identified by an index into the world — see
 * the possession note on `MapProp` — and it means client and server index the
 * same solid by the same number without exchanging anything.
 *
 * Prop hulls ignore `yaw`. A rotated box is not an AABB, and approximating one
 * with its bounding box would make collision disagree with what is drawn. Prop
 * hulls are therefore square in plan (`halfWidth === halfDepth`) by convention,
 * which the validator enforces, so yaw is purely visual.
 */
export function mapSolids(map: GameMap, catalogue: PropCatalogue): Aabb[] {
  const out: Aabb[] = map.solids.map(solidToAabb);
  for (const p of map.props) {
    const def = catalogue.get(p.prop);
    // Unknown ids are a validation error; skipping keeps this total for
    // callers that build a world for inspection before validating.
    if (def !== undefined) out.push(propToAabb(p, def));
  }
  return out;
}

export function buildWorld(map: GameMap, catalogue: PropCatalogue): StaticWorld {
  return new StaticWorld(mapSolids(map, catalogue));
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface MapError {
  /** Dotted path to the offending value, e.g. "solids[3].h". */
  where: string;
  message: string;
}

function finite(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function inWorldBounds(a: Aabb): boolean {
  return (
    a.minX >= WORLD_MIN_XZ && a.maxX <= WORLD_MAX_XZ &&
    a.minZ >= WORLD_MIN_XZ && a.maxZ <= WORLD_MAX_XZ &&
    a.minY >= WORLD_MIN_Y && a.maxY <= WORLD_MAX_Y
  );
}

/**
 * Every way a map can be wrong that we can detect without playing it.
 *
 * Returns all errors rather than throwing on the first, because a map is
 * authored in bulk by a tool and fixing one problem per run is miserable.
 */
export function validateMap(map: GameMap, catalogue: PropCatalogue): MapError[] {
  const errors: MapError[] = [];
  const fail = (where: string, message: string) => errors.push({ where, message });

  if (map.formatVersion !== MAP_FORMAT_VERSION) {
    fail(
      "formatVersion",
      `map is format v${map.formatVersion}, this build reads v${MAP_FORMAT_VERSION}`,
    );
  }
  if (typeof map.name !== "string" || map.name.length === 0) {
    fail("name", "maps must be named; the name appears in the hash and in the UI");
  }

  // --- solids --------------------------------------------------------------
  map.solids.forEach((s, i) => {
    const at = `solids[${i}]`;
    if (![s.x, s.y, s.z, s.w, s.h, s.d].every(finite)) {
      fail(at, "every field must be a finite number");
      return;
    }
    // The load-bearing rule. Collision tests only the hull's destination, so a
    // solid thinner than a sub-step is one a fast mover passes clean through.
    // See MAX_SUBSTEP_DISTANCE in rules.ts.
    for (const [axis, value] of [["w", s.w], ["h", s.h], ["d", s.d]] as const) {
      if (value < MIN_SOLID_THICKNESS) {
        fail(
          `${at}.${axis}`,
          `${value}m is thinner than MIN_SOLID_THICKNESS (${MIN_SOLID_THICKNESS}m); ` +
            "a player at terminal velocity would tunnel through it",
        );
      }
    }
    if (!inWorldBounds(solidToAabb(s))) {
      fail(
        at,
        "extends outside the world bounds, which are the quantisation range of " +
          "the wire format — geometry out here cannot be described in a snapshot",
      );
    }
  });

  // --- props ---------------------------------------------------------------
  map.props.forEach((p, i) => {
    const at = `props[${i}]`;
    if (![p.x, p.y, p.z, p.yaw].every(finite)) {
      fail(at, "every field must be a finite number");
      return;
    }
    const def = catalogue.get(p.prop);
    if (def === undefined) {
      fail(`${at}.prop`, `no prop with id ${p.prop} in the catalogue`);
      return;
    }
    if (!inWorldBounds(propToAabb(p, def))) {
      fail(at, "prop extends outside the world bounds");
    }
  });

  // --- catalogue -----------------------------------------------------------
  for (const def of catalogue.values()) {
    const at = `catalogue[${def.id}]`;
    if (!Number.isInteger(def.id) || def.id < 1 || def.id > 255) {
      fail(at, "prop ids must be integers in 1..255; 0 means 'undisguised player'");
    }
    const { halfWidth, halfDepth, height } = def.hull;
    if (![halfWidth, halfDepth, height].every(finite)) {
      fail(`${at}.hull`, "hull dimensions must be finite");
      continue;
    }
    // See the yaw note on mapSolids: collision is axis-aligned, so a prop whose
    // footprint is not square would collide differently depending on how it was
    // rotated, while the collision box stayed put.
    if (halfWidth !== halfDepth) {
      fail(
        `${at}.hull`,
        "prop footprints must be square (halfWidth === halfDepth); collision is " +
          "axis-aligned and cannot rotate with the mesh",
      );
    }
    if (halfWidth * 2 < MIN_SOLID_THICKNESS || height < MIN_SOLID_THICKNESS) {
      fail(`${at}.hull`, `prop hull is thinner than MIN_SOLID_THICKNESS (${MIN_SOLID_THICKNESS}m)`);
    }
  }

  // --- spawns --------------------------------------------------------------
  const solids = mapSolids(map, catalogue);
  map.spawns.forEach((sp, i) => {
    const at = `spawns[${i}]`;
    if (![sp.x, sp.y, sp.z, sp.yaw].every(finite)) {
      fail(at, "every field must be a finite number");
      return;
    }
    if (sp.team !== "hunter" && sp.team !== "hider" && sp.team !== "any") {
      fail(`${at}.team`, `unknown team "${sp.team}"`);
    }
    // A spawn inside geometry is a player who cannot move, reported as "the
    // game is broken" and diagnosed as anything but a level-data problem.
    const hull: Aabb = {
      minX: sp.x - DEFAULT_HULL.halfWidth,
      minY: sp.y,
      minZ: sp.z - DEFAULT_HULL.halfDepth,
      maxX: sp.x + DEFAULT_HULL.halfWidth,
      maxY: sp.y + DEFAULT_HULL.height,
      maxZ: sp.z + DEFAULT_HULL.halfDepth,
    };
    if (!inWorldBounds(hull)) {
      fail(at, "a player standing here would be outside the world bounds");
    }
    const blocking = solids.findIndex((s) => aabbOverlaps(hull, s));
    if (blocking !== -1) {
      fail(at, `a player standing here would be inside solid #${blocking}`);
    }
  });

  const hunters = map.spawns.filter((s) => s.team === "hunter" || s.team === "any").length;
  const hiders = map.spawns.filter((s) => s.team === "hider" || s.team === "any").length;
  if (hunters === 0) fail("spawns", "no spawn point a hunter could use");
  if (hiders === 0) fail("spawns", "no spawn point a hider could use");

  return errors;
}

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * Deterministic serialisation: keys sorted, no incidental whitespace.
 *
 * `JSON.stringify` preserves insertion order, so two structurally identical
 * maps that were built by different code paths would stringify differently and
 * hash differently — which would reject clients over nothing. Number formatting
 * is safe to rely on: JS specifies the shortest round-tripping representation,
 * so every engine renders a given double identically.
 */
export function canonicalise(value: unknown): string {
  if (value === null) return "null";

  switch (typeof value) {
    case "boolean":
      return value ? "true" : "false";
    case "number":
      if (!Number.isFinite(value)) {
        throw new RangeError("map data contains a non-finite number");
      }
      // -0 and 0 are the same position but stringify differently. Folding them
      // avoids a hash mismatch that no author could ever see in their data.
      return Object.is(value, -0) ? "0" : String(value);
    case "string":
      return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(canonicalise).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of Object.keys(record).sort()) {
      const v = record[key];
      // Match JSON.stringify: an explicitly-undefined property is absent.
      if (v === undefined) continue;
      parts.push(`${JSON.stringify(key)}:${canonicalise(v)}`);
    }
    return `{${parts.join(",")}}`;
  }

  throw new TypeError(`cannot canonicalise a ${typeof value}`);
}

function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Identity of a map, for the join handshake, as a raw u32.
 *
 * Covers the catalogue as well as the map, because prop hulls are simulation
 * input and prop meshes are what the whole game is about looking at.
 *
 * This is an *agreement* check, not a security boundary — a modified client can
 * obviously send whatever hash it likes. That costs it nothing, because the
 * server simulates against its own map regardless; the hash exists so that an
 * honest client on a stale build is told so instead of quietly desyncing. FNV
 * is chosen accordingly: short, dependency-free, and not asked to resist
 * anything.
 *
 * This is the value that goes on the wire (HelloMsg.mapHash / WelcomeMsg
 * .mapHash) — a u32, not the hex string, since the string exists only for logs
 * and UI. Use `mapHashHex` for those.
 */
export function mapHash(map: GameMap, catalogue: PropCatalogue): number {
  const defs = [...catalogue.values()].sort((a, b) => a.id - b.id);
  return fnv1a32(canonicalise({ map, catalogue: defs }));
}

/** `mapHash`, formatted for logs and UI. Never put on the wire — see mapHash. */
export function mapHashHex(map: GameMap, catalogue: PropCatalogue): string {
  return mapHash(map, catalogue).toString(16).padStart(8, "0");
}
